import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';
import { App } from 'App.tsx';

import {
    adminV2AppRoot,
    authRoute,
    authUserTypeSelect,
    bankingPlaidCallbackRoute,
    businessAppRoot,
    businessCoOwner,
    businessCoworkerInvite,
    businessCoworkerSignUp,
    businessSignUp,
    businessTeamAppRoot,
    clientAppRoot,
    clientBankingHandlerRoute,
    coworkerAppRoot,
    googleUserTypeSelect,
    investorAppRoot,
    investorRegistrationClosedRoute,
    investorSignUp,
    maintenanceRoute,
    preQualLanding,
    secondaryInvestor,
    squareLanding,
    userConfirmEmail,
} from 'constants.ts';
import { dealDetailsPath, ownerDetailsPath } from '@AdminV2/routes/paths.ts';
import { AuthWrapper } from '@Client/auth/AuthWrapper.tsx';
import { AuthUserTypeSelect, SignIn, SignUp } from '@Client/auth/lazyAuthPages.ts';
import { InvestorRegistrationGuard } from '@/Components/InvestorRegistrationGuard.tsx';
import { LightRouteShell } from '@/Components/LightRouteShell.tsx';
import { MaintenanceGuard } from '@/Components/MaintenanceGuard.tsx';
import { MaintenancePage } from '@/Components/MaintenancePage.tsx';
import { NotFoundPage } from '@/Components/NotFoundPage.tsx';
import { USER_TYPES } from '@/constants.ts';
import { businessPaymentApprovalsPath } from 'Apps/business-app/constants.ts';
import { ExternalLinks } from 'Components/ExternalLinks.tsx';
import { RouterErrorComponent } from 'Components/RouterErrorComponent.tsx';

const ClientApp = lazy(() => import('Apps/client-app'));
const InvestorApp = lazy(() => import('Apps/investor-app/AppRoot.tsx'));
const CoworkerApp = lazy(() => import('Apps/coworker-app/AppRoot.tsx'));
const BusinessApp = lazy(() => import('Apps/business-app/AppRoot.tsx'));
const BusinessTeamApp = lazy(() => import('Apps/business-team/AppRoot.tsx'));
const AdminV2 = lazy(() => import('Apps/admin-v2'));
const SquareExpansionCapital = lazy(
    () => import('Apps/business-app/pages/SquareExpansionCapital/SquareExpansionCapital.tsx')
);
const PreQual = lazy(() => import('Apps/business-app/pages/PreQual/PreQual.tsx'));

const UserHandler = lazy(() => import('Apps/client-app/components/UserHandler.tsx'));
const ClientBankingHandler = lazy(
    () => import('Apps/client-app/components/ClientBankingHandler.tsx')
);

const UserConfirmEmail = lazy(() =>
    import('@Client/UserConfirmEmail.tsx').then((m) => ({ default: m.UserConfirmEmail }))
);
const GoogleUserTypeSelect = lazy(() =>
    import('@Client/auth/GoogleUserTypeSelect.tsx').then((m) => ({
        default: m.GoogleUserTypeSelect,
    }))
);
const BusinessCoOwnerInvite = lazy(() =>
    import('@Client/pages/Invites/business-co-owner/BusinessCoOwnerInvite.tsx').then((m) => ({
        default: m.BusinessCoOwnerInvite,
    }))
);
const SecondaryInvestorInvite = lazy(() =>
    import('@Client/pages/Invites/secondary-investor/SecondaryInvestorInvite.tsx').then((m) => ({
        default: m.SecondaryInvestorInvite,
    }))
);
const CoworkerSignUp = lazy(() =>
    import('@Client/components/auth/CoworkerSignUp.tsx').then((m) => ({
        default: m.CoworkerSignUp,
    }))
);

export const routes: RouteObject[] = [
    {
        path: '/',
        element: <App />,
        children: [
            {
                path: adminV2AppRoot + '/*',
                element: <AdminV2 />,
            },
            {
                path: '/adminV2/*',
                element: (
                    <ExternalLinks<'*'>>
                        {(params, search, hash) =>
                            ExternalLinks.redirect({
                                pathname: params['*']
                                    ? `${adminV2AppRoot}/${params['*']}`
                                    : adminV2AppRoot,
                                search,
                                hash,
                            })
                        }
                    </ExternalLinks>
                ),
            },
            {
                path: maintenanceRoute,
                element: <MaintenancePage />,
            },
            {
                path: investorRegistrationClosedRoute,
                element: <MaintenancePage variant="investor_registration" />,
            },
            {
                element: <MaintenanceGuard />,
                children: [
                    {
                        path: clientAppRoot,
                        element: <ClientApp />,
                        errorElement: <RouterErrorComponent />,
                        children: [
                            { index: true, element: <UserHandler /> },
                            {
                                path: clientBankingHandlerRoute,
                                element: <ClientBankingHandler />,
                            },
                            {
                                path: investorAppRoot + '/*',
                                element: <InvestorApp />,
                            },
                            {
                                path: businessAppRoot + '/*',
                                element: <BusinessApp />,
                            },
                            {
                                path: coworkerAppRoot + '/*',
                                element: <CoworkerApp />,
                            },
                            {
                                path: businessTeamAppRoot + '/*',
                                element: <BusinessTeamApp />,
                            },
                            {
                                path: googleUserTypeSelect,
                                element: <GoogleUserTypeSelect />,
                            },
                        ],
                    },
                    {
                        path: secondaryInvestor,
                        element: (
                            <LightRouteShell>
                                <SecondaryInvestorInvite />
                            </LightRouteShell>
                        ),
                    },
                    {
                        path: businessCoOwner,
                        element: (
                            <LightRouteShell>
                                <BusinessCoOwnerInvite />
                            </LightRouteShell>
                        ),
                    },
                    {
                        path: '/coworker-signup',
                        element: <CoworkerSignUp userType={USER_TYPES.coworker} />,
                    },
                    {
                        path: businessCoworkerSignUp,
                        element: <CoworkerSignUp userType={USER_TYPES.business_coworker} />,
                    },
                    {
                        element: <AuthWrapper />,
                        children: [
                            {
                                path: authRoute,
                                element: <SignIn />,
                            },
                            {
                                path: authUserTypeSelect,
                                element: <AuthUserTypeSelect />,
                            },
                            {
                                element: <InvestorRegistrationGuard />,
                                children: [
                                    {
                                        path: investorSignUp,
                                        element: <SignUp userType={USER_TYPES.investor} />,
                                    },
                                ],
                            },
                            {
                                path: businessSignUp,
                                element: <SignUp userType={USER_TYPES.business_owner} />,
                            },
                        ],
                    },
                    {
                        path: squareLanding,
                        element: (
                            <LightRouteShell>
                                <SquareExpansionCapital />
                            </LightRouteShell>
                        ),
                    },
                    {
                        path: preQualLanding,
                        element: <PreQual />,
                    },
                    {
                        path: userConfirmEmail,
                        element: <UserConfirmEmail />,
                    },
                ],
            },
            //external links, change it carefully
            {
                // the link in the authorization email, built by send_payback_consent_request in
                // sendgrid_app/tasks.py - this path is the other end of it. The page itself is
                // behind the business owner's sign-in, so the link carries nothing but the path
                path: '/payment-approvals',
                element: <ExternalLinks redirect={businessPaymentApprovalsPath} />,
            },
            {
                // review link in the support alert from
                // send_business_owner_submitted_registration_request
                path: 'pending-owner/:auth0id',
                element: (
                    <ExternalLinks<'auth0id'>>
                        {(params, search, hash) =>
                            ExternalLinks.redirect({
                                pathname: ownerDetailsPath(params.auth0id || ''),
                                search,
                                hash,
                            })
                        }
                    </ExternalLinks>
                ),
            },
            {
                // business_owner_link in the support alert from send_deal_request
                path: 'deal-request/:auth0id',
                element: (
                    <ExternalLinks<'auth0id'>>
                        {(params, search, hash) =>
                            // deal creation from the owner is gone - land on the owner's page
                            ExternalLinks.redirect({
                                pathname: ownerDetailsPath(params.auth0id || ''),
                                search,
                                hash,
                            })
                        }
                    </ExternalLinks>
                ),
            },
            {
                // nothing in the backend sends this any more; kept for links already sent
                path: 'pending-deal/:dealId',
                element: (
                    <ExternalLinks<'dealId'>>
                        {(params, search, hash) =>
                            ExternalLinks.redirect({
                                pathname: dealDetailsPath(Number(params.dealId)),
                                search,
                                hash,
                            })
                        }
                    </ExternalLinks>
                ),
            },
            {
                // invite email from send_entity_coworker_invite, carrying the invite code
                path: 'coworker-invite',
                element: (
                    <ExternalLinks>
                        {(_, search) =>
                            ExternalLinks.redirect('/coworker-signup', {
                                code: new URLSearchParams(search).get('code'),
                            })
                        }
                    </ExternalLinks>
                ),
            },
            {
                path: businessCoworkerInvite,
                element: (
                    <ExternalLinks>
                        {(_, search) =>
                            ExternalLinks.redirect(businessCoworkerSignUp, {
                                code: new URLSearchParams(search).get('code'),
                            })
                        }
                    </ExternalLinks>
                ),
            },
            {
                // invite email from send_secondary_investor_invite; the code travels in the query
                path: 'entity-investor',
                element: (
                    <ExternalLinks>
                        {() =>
                            ExternalLinks.redirect({
                                pathname: secondaryInvestor,
                            })
                        }
                    </ExternalLinks>
                ),
            },
            {
                // where Plaid returns from its OAuth screen: useConnectPlaid hands it this url as
                // redirect_uri, and it is registered in the Plaid dashboard
                path: bankingPlaidCallbackRoute,
                element: <ExternalLinks redirect={clientBankingHandlerRoute} />,
            },
            //
            {
                path: '*',
                element: <NotFoundPage />,
            },
        ],
        errorElement: <RouterErrorComponent />,
    },
];
